/**
 * Contagem de mensagens não lidas no chat de grupo e DMs.
 * Usa useState para ser compartilhado entre sidebar e página de chat.
 * Fallback via polling quando WebSocket não está ativo.
 */

// Vitest não tem o auto-import useState do Nuxt — usa ref como fallback.
function _state<T>(key: string, init: () => T): Ref<T> {
  if (typeof useState !== 'undefined') return useState<T>(key, init)
  return ref(init()) as Ref<T>
}

export const useChatUnread = () => {
  const total    = _state('chat-unread-total', () => 0 as number)
  const dm       = _state('chat-unread-dm',    () => ({} as Record<number, number>))
  const lastSeen = _state('chat-last-seen',    () => new Date().toISOString())
  let pollingTimer: ReturnType<typeof setInterval> | null = null

  function addUnread(senderId?: number, isDirect = false) {
    total.value++
    if (isDirect && senderId) {
      dm.value = { ...dm.value, [senderId]: (dm.value[senderId] ?? 0) + 1 }
    }
  }

  function resetAll() {
    total.value = 0
    dm.value = {}
    lastSeen.value = new Date().toISOString()
  }

  function resetDm(userId: number) {
    const next: Record<number, number> = { ...dm.value }
    delete next[userId]
    dm.value = next
    total.value = Object.values(next).reduce((s: number, v: number) => s + v, 0)
  }

  function startPolling() {
    if (pollingTimer) return
    pollingTimer = setInterval(async () => {
      try {
        const api = useApi()
        const { data } = await api.get('/chat/mensagens/unread/', {
          params: { since: lastSeen.value },
        })
        total.value = (data.total ?? 0) as number
        // Backend retorna chaves como string ("1", "2") — converte para number
        const rawDm: Record<string, number> = data.dm ?? {}
        dm.value = Object.fromEntries(
          Object.entries(rawDm).map(([k, v]) => [Number(k), v as number])
        )
      } catch {
        // API pode não estar disponível
      }
    }, 30000)
  }

  function stopPolling() {
    if (pollingTimer) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
  }

  return { total, dm, addUnread, resetAll, resetDm, startPolling, stopPolling }
}
